import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { css } from '@compiled/react';

import Heading from '@atlaskit/heading/heading';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { AGENT_CARDS } from './agentCards.config.ts';
import { AgentCardRow } from './AgentCardRow.tsx';
import { i18n } from './messages.ts';

const sectionStyles = css({
	marginBottom: token('space.300'),
});

const headerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	marginBottom: token('space.150'),
});

/**
 * Persisted (server-synced) storage key holding the set of dismissed agent
 * cards. The value is a JSON map keyed by each card's stable `key`, e.g.
 * `{ "daily-brief": 1, "project-planner": 1 }`. Using a single key with a map
 * (rather than one key per agent) keeps all dismissals in one value and mirrors
 * the existing `AgentAccessWarning` dismissal pattern.
 */
const DISMISSED_CARDS_KEY = PERSISTED_KEYS_ON_SERVER.PERSISTED_FOR_YOU_AGENT_CARD_NUDGE_DISMISSED;

/** Read the persisted set of dismissed card keys, tolerating absent/corrupt data. */
const readDismissedKeys = (): Set<string> => {
	try {
		const stored = JSON.parse(localStorage?.getItem(DISMISSED_CARDS_KEY) ?? '{}') ?? {};
		return new Set(Object.keys(stored));
	} catch {
		return new Set();
	}
};

/**
 * Persist that `key` has been dismissed and return the resulting set of
 * dismissed keys.
 *
 * The merge re-reads the persisted value immediately before writing so that
 * dismissals made in another tab (and already committed to storage) are not
 * clobbered. The returned set is the source of truth the caller should adopt,
 * keeping in-memory state consistent with what was actually written. If storage
 * is unavailable, we fall back to merging onto `fallback` so the in-memory hide
 * still applies for this session.
 */
const persistDismissedKey = (key: string, fallback: ReadonlySet<string>): Set<string> => {
	try {
		const existing = JSON.parse(localStorage?.getItem(DISMISSED_CARDS_KEY) ?? '{}') ?? {};
		const merged = { ...existing, [key]: 1 };
		localStorage?.setItem(DISMISSED_CARDS_KEY, JSON.stringify(merged));
		return new Set(Object.keys(merged));
	} catch {
		return new Set(fallback).add(key);
	}
};

/**
 * "Rovo Agents for you" section on Confluence Home / For You.
 *
 * Dismissing a card removes it from the view and the row reflows (3 → 2 → 1
 * cards). When every card is dismissed the whole section unmounts so the next
 * section takes its place. Dismissals are written to a server-synced persisted
 * storage key so a dismissed card stays hidden across reloads and sessions.
 *
 * Eligibility/gating and grid + UFOSegment placement are owned by ForYou.tsx,
 * which only mounts this component when eligible.
 */
export const RovoAgentsForYou: FC = () => {
	// Read persisted dismissals synchronously so the post-hydration render shows
	// the correct (already-filtered) cards without a flash. To stay hydration
	// safe, the section renders nothing during SSR and the first client render
	// (see renderServerPlaceholder below), so the browser-only persisted state
	// only affects output after hydration.
	const [dismissedKeys, setDismissedKeys] = useState<ReadonlySet<string>>(readDismissedKeys);

	const handleDismiss = useCallback(
		(key: string) => {
			setDismissedKeys(persistDismissedKey(key, dismissedKeys));
		},
		[dismissedKeys],
	);

	const visibleCards = useMemo(
		() => AGENT_CARDS.filter((card) => !dismissedKeys.has(card.key)),
		[dismissedKeys],
	);

	const renderServerPlaceholder = useRenderServerPlaceholder();

	// Render nothing during SSR / first client render to avoid a hydration
	// mismatch from the browser-only persisted dismissals, and once every card is
	// dismissed so the following section takes the space.
	if (renderServerPlaceholder || visibleCards.length === 0) {
		return null;
	}

	return (
		<section css={sectionStyles} data-testid="rovo-agents-for-you">
			<div css={headerStyles}>
				<Heading size="xsmall" as="h2">
					<FormattedMessage {...i18n.sectionTitle} />
				</Heading>
				<Lozenge appearance="new">
					<FormattedMessage {...i18n.labsLozenge} />
				</Lozenge>
			</div>
			<AgentCardRow cards={visibleCards} onDismiss={handleDismiss} />
		</section>
	);
};
